import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import { LinkButton } from '@adsk/alloy-react-button';
import EmptyState from '@adsk/alloy-react-empty-state';
import { IllustrationType } from '@adsk/alloy-react-illustration';
import { useLocale } from '@adsk/alloy-react-locale';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

type DefaultContentProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  disabled?: boolean;
  hideIllustration?: boolean;
  title?: ReactNode;
  description?: ReactNode;
  illustrationType?: IllustrationType;
  open?: () => void;
  linkText?: ReactNode;
};

const DefaultContent = React.forwardRef<HTMLDivElement, DefaultContentProps>(
  (
    {
      style = { padding: 0 },
      className,
      open,
      disabled,
      title,
      description,
      illustrationType,
      hideIllustration,
      linkText,
      ...rest
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const theme = useTheme();
    const contentDescriptionTokens =
      theme.tokens?.component?.dropzone?.['content-description'];

    return (
      <EmptyState
        {...rest}
        hideIllustration={hideIllustration}
        ref={ref}
        title={title || T.translate('DRAG_FILES_HERE')}
        description={
          description ? (
            <span css={contentDescriptionTokens}>{description}</span>
          ) : undefined
        }
        className={className}
        style={style}
        illustrationType={
          illustrationType ||
          EmptyState.ILLUSTRATION_TYPES.FOLDER_PDF_CLOSED_FIST_GREY
        }
      >
        <LinkButton
          disabled={disabled}
          onClick={(e) => {
            open && open();
            // that is because the entire dropzone is clickable
            e.stopPropagation();
          }}
        >
          {linkText || T.translate('CHOOSE_FILES')}
        </LinkButton>
      </EmptyState>
    );
  },
);

DefaultContent.displayName = 'DefaultContent';

DefaultContent.propTypes = {
  ...EmptyState.propTypes,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Function that opens file browser */
  open: PropTypes.func,
  /** Whether dropzone is disabled */
  disabled: PropTypes.bool,
};

export default Object.assign(DefaultContent, {
  ILLUSTRATION_TYPES: EmptyState.ILLUSTRATION_TYPES,
});
